import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "../i18n";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Flag from "./Flag";

export default function LanguageSwitcher({ variant = "header" }) {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const change = async (code) => {
    if (code === i18n.language) {
      setOpen(false);
      return;
    }
    i18n.changeLanguage(code);
    setOpen(false);
    // Persisterer på server hvis innlogget
    if (user && user !== false) {
      try {
        await api.put("/me/ui-language", { ui_language: code });
      } catch {
        // Stille feil – localStorage holder valget uansett
      }
    }
  };

  const compact = variant === "header";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={
          compact
            ? "flex items-center gap-1.5 px-2 py-1.5 text-[10.5px] font-manrope uppercase tracking-[0.12em] text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
            : "flex items-center gap-2 px-3 py-2 text-sm border border-white/10 hover:border-[var(--magenta)] text-[var(--cream)]"
        }
        data-testid="language-switcher-trigger"
        title={current.name}
      >
        <Flag code={current.code} />
        <span>{current.code.toUpperCase()}</span>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 w-44 bg-[var(--noir-elev)] border border-white/10 shadow-xl z-50"
          data-testid="language-switcher-menu"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => change(l.code)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-xs font-manrope tracking-[0.1em] hover:bg-white/5 transition-colors ${
                l.code === i18n.language ? "text-[var(--magenta)]" : "text-[var(--cream)]"
              }`}
              data-testid={`language-option-${l.code}`}
            >
              <span className="flex items-center gap-2">
                <Flag code={l.code} />
                <span>{l.name}</span>
                <span className="text-[var(--cream-faint)] text-[10px]">{l.code.toUpperCase()}</span>
              </span>
              {l.code === i18n.language && <Check className="h-3 w-3" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
